import { useContext, useState } from "react";

import { Alert, Divider, Stack } from "@mui/material";
import { ClientContent, ClientFileInfoLineContext, SaveBar } from "@shared";

import { useRefreshClaimsSnapshot } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/srv-proceeding-tab/hooks/useRefreshClaimsSnapshot";
import { useSrvProceedingForm } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/srv-proceeding-tab/hooks/useSrvProceedingForm";
import { useTranslation } from "@/translations";

import { ApplicationSection } from "../application-section/ApplicationSection";
import { AssetSnapshotSection } from "../asset-snapshot-section/AssetSnapshotSection";
import { ClaimsSnapshotSection } from "../claims-snapshot-section/ClaimsSnapshotSection";
import { CourseTable } from "../course-table/CourseTable";
import { ProceedingDataSection } from "../proceeding-data-section/ProceedingDataSection";
import { QuotaCostsSection } from "../quota-costs-section/QuotaCostsSection";

interface Props {
  clientId: string;
  /** The opened Verfahren; undefined while a new one is drafted. */
  proceedingId: string | undefined;
  onCreated: (proceedingId: string) => void;
  onCancelCreate: () => void;
}

export function SrvProceedingDetail({ clientId, proceedingId, onCreated, onCancelCreate }: Props) {
  const { t } = useTranslation();
  const isCreating = proceedingId === undefined;
  const { formik, proceeding, isError, isInactiveTrusteeSelected, isInactiveInsolvencyAdministratorSelected } =
    useSrvProceedingForm({ clientId, proceedingId, onCreated });
  const refresh = useRefreshClaimsSnapshot(clientId);
  const infoLine = useContext(ClientFileInfoLineContext)?.();
  // The participant searches keep the name they show in state of their own, which a Formik reset does
  // not reach; remounting the sections lets every field start again from the saved values.
  const [resetCount, setResetCount] = useState(0);

  const discardChanges = () => {
    formik.resetForm();
    setResetCount((count) => count + 1);
  };

  // Until a stored Verfahren is loaded the form holds the empty draft values; saving those would blank it.
  if (!isCreating && !proceeding) {
    return isError ? (
      <ClientContent>
        <Stack bgcolor="background.paper" flex={1} p={2}>
          <Alert severity="error">{t("clients.client.debtSettlement.srvProceeding.loadFailed")}</Alert>
        </Stack>
      </ClientContent>
    ) : null;
  }

  return (
    <ClientContent
      formik={formik}
      customActions={
        <SaveBar
          sx={{ flex: 1, p: 0 }}
          resetLabel={t("common.resetChanges")}
          // A draft is never saved by resetting it, so resetting leaves it: back to the Verfahren
          // that was open before.
          onReset={
            isCreating
              ? () => {
                  formik.resetForm();
                  onCancelCreate();
                }
              : discardChanges
          }
          resetDisabled={formik.isSubmitting || (!isCreating && !formik.dirty)}
          saveLabel={t("common.save")}
          // Every field of a Verfahren is optional, so a draft can be saved untouched; it then takes its
          // snapshots and is listed as "Verfahren".
          saveDisabled={
            formik.isSubmitting ||
            (!isCreating && !formik.dirty) ||
            isInactiveTrusteeSelected ||
            isInactiveInsolvencyAdministratorSelected
          }
          info={infoLine}
        />
      }
    >
      <Stack key={resetCount} bgcolor="background.paper" flex={1} p={2} gap={4} divider={<Divider />}>
        <ApplicationSection formik={formik} />
        <ProceedingDataSection
          formik={formik}
          proceeding={proceeding}
          isInactiveTrusteeSelected={isInactiveTrusteeSelected}
          isInactiveInsolvencyAdministratorSelected={isInactiveInsolvencyAdministratorSelected}
        />
        {proceeding && (
          <ClaimsSnapshotSection
            snapshot={proceeding.claimsSnapshot}
            onRefresh={() => refresh.mutate(proceeding.id)}
            isRefreshing={refresh.isPending}
          />
        )}
        {proceeding && <AssetSnapshotSection snapshot={proceeding.assetSnapshot} />}
        <QuotaCostsSection formik={formik} />
        <CourseTable formik={formik} />
      </Stack>
    </ClientContent>
  );
}
